
import React, { useState } from 'react';
import { StudentCounselModel, MemberModel } from '../../../../models';
import { User, Calendar, Clock, MessageSquare, Trash2, Send, X, CheckCircle2 } from 'lucide-react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { getAuth } from 'firebase/auth';

interface CounselViewProps {
    counsel: StudentCounselModel;
    member?: MemberModel;
    onClose: () => void;
    onUpdate: () => void;
}

const CounselView: React.FC<CounselViewProps> = ({ counsel, member, onClose, onUpdate }) => {
    const [reply, setReply] = useState(counsel.reply || '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSaveReply = async () => {
        setIsSubmitting(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            const authorName = user?.displayName || user?.email?.split('@')[0] || '관리자';

            await updateDoc(doc(db, 'studentCounsel', counsel.id), {
                reply,
                repliedAt: Date.now(),
                repliedBy: authorName,
                isStudentRead: false // Mark as unread for student
            });
            alert('답변이 저장되었습니다.');
            onUpdate && onUpdate();
        } catch (error) {
            console.error('Error saving reply:', error);
            alert('답변 저장 실패');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('정말로 이 상담 내역을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'studentCounsel', counsel.id));
            onClose();
        } catch (error) {
            console.error('Error deleting counsel:', error);
            alert('삭제 실패');
        }
    };

    // Helper to safe display date
    const formatDate = (val: any) => {
        if (typeof val === 'number') return new Date(val).toLocaleDateString();
        return String(val);
    };

    return (
        <div className="h-full flex flex-col bg-white w-full animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                        {member?.name?.[0] || <User size={20} />}
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-slate-800">{member?.name || '알 수 없는 사용자'}</h2>
                        <div className="flex items-center gap-2 text-xs font-medium text-slate-400">
                            <span className="flex items-center gap-1">
                                <Calendar size={10} />
                                {formatDate(counsel.date)}
                            </span>
                            <span>•</span>
                            <span className="text-indigo-500">{counsel.tag}</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleDelete}
                        className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                        title="상담 내역 삭제"
                    >
                        <Trash2 size={18} />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Content Scroll Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
                {/* Question Section */}
                <div className="space-y-2">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">상담 내용</h3>
                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        <h4 className="text-lg font-bold text-slate-800 mb-2">{counsel.title}</h4>
                        <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">
                            {counsel.content}
                        </p>
                    </div>
                </div>

                {/* Reply Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                            <MessageSquare size={14} />
                            관리자 답변
                        </h3>
                        <div className="flex items-center gap-3">
                            {counsel.repliedAt && (
                                <div className="text-right">
                                    <span className="text-xs font-bold text-slate-600 block">
                                        {counsel.repliedBy || '관리자'}
                                    </span>
                                    <span className="text-[10px] text-emerald-500 font-medium flex items-center gap-1 justify-end">
                                        <CheckCircle2 size={10} />
                                        {new Date(counsel.repliedAt).toLocaleDateString()} 완료
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="relative group">
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="답변 내용을 입력하세요..."
                            className="w-full h-64 bg-slate-50 border-2 border-slate-100 rounded-2xl p-6 text-slate-700 font-medium focus:border-indigo-500 focus:ring-0 transition-all resize-none outline-none leading-relaxed"
                        />
                        <div className="absolute bottom-4 right-4">
                            <button
                                onClick={handleSaveReply}
                                disabled={isSubmitting}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2"
                            >
                                {isSubmitting ? <Clock size={16} className="animate-spin" /> : <Send size={16} />}
                                {counsel.reply ? '답변 수정' : '답변 등록'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CounselView;
